import { useTranslation } from '@kostbar/i18n'
import {
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pagination as UIPagination,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type DataTablePaginationProps = {
  pageIndex: number
  pageSize: number
  pageCount?: number
  total?: number
  pageSizeOptions: Array<number>
  onPageChange: (index: number) => void
  onPageSizeChange: (size: number) => void
}

/**
 * Generates pagination page numbers with ellipsis for large page counts.
 * Shows: 1 ... 4 5 [6] 7 8 ... 100
 */
function getPageNumbers(
  currentPage: number,
  totalPages: number,
): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    // Show all pages if 7 or fewer
    return Array.from({ length: totalPages }, (_, i) => i)
  }

  const pages: Array<number | 'ellipsis'> = []
  const delta = 2 // Pages to show around current page

  // Always show first page
  pages.push(0)

  // Calculate start and end of middle range
  let rangeStart = Math.max(1, currentPage - delta)
  let rangeEnd = Math.min(totalPages - 2, currentPage + delta)

  // Adjust range if too close to edges
  if (currentPage - delta < 2) {
    rangeEnd = Math.min(totalPages - 2, 5)
  }
  if (currentPage + delta > totalPages - 3) {
    rangeStart = Math.max(1, totalPages - 6)
  }

  // Add ellipsis before middle range if needed
  if (rangeStart > 1) {
    pages.push('ellipsis')
  }

  // Add middle range
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i)
  }

  // Add ellipsis after middle range if needed
  if (rangeEnd < totalPages - 2) {
    pages.push('ellipsis')
  }

  // Always show last page if more than 1 page
  if (totalPages > 1) {
    pages.push(totalPages - 1)
  }

  return pages
}

export function DataTablePagination({
  pageIndex,
  pageSize,
  pageCount,
  total,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: DataTablePaginationProps) {
  const { t: tUi } = useTranslation('ui')
  const canPrev = pageIndex > 0
  const canNext = pageCount !== undefined ? pageIndex < pageCount - 1 : true

  const start = total !== undefined ? pageIndex * pageSize + 1 : undefined
  const end =
    total !== undefined
      ? Math.min((pageIndex + 1) * pageSize, total)
      : undefined

  const pageNumbers =
    pageCount !== undefined ? getPageNumbers(pageIndex, pageCount) : []

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="text-sm text-muted-foreground">
        {total !== undefined && (
          <span>
            {tUi('pagination.showing')} {start}–{end} {tUi('pagination.of')}{' '}
            {total}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {tUi('pagination.rowsPerPage')}
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="w-[84px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <UIPagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                aria-disabled={!canPrev}
                onClick={(e) => {
                  e.preventDefault()
                  if (canPrev) onPageChange(pageIndex - 1)
                }}
                href="#"
                className={
                  !canPrev ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>

            {pageNumbers.map((page, idx) => (
              <PaginationItem key={`${page}-${idx}`}>
                {page === 'ellipsis' ? (
                  <span className="px-4 text-muted-foreground">…</span>
                ) : (
                  <PaginationLink
                    href="#"
                    isActive={page === pageIndex}
                    onClick={(e) => {
                      e.preventDefault()
                      onPageChange(page)
                    }}
                    className="cursor-pointer"
                  >
                    {page + 1}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                aria-disabled={!canNext}
                onClick={(e) => {
                  e.preventDefault()
                  if (canNext) onPageChange(pageIndex + 1)
                }}
                href="#"
                className={
                  !canNext ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </UIPagination>
      </div>
    </div>
  )
}
